import { useState, type ReactElement } from 'react'
import { isNotEmpty, type TeamData, type TeamId } from '@terros/common'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useProfile, useRubrics, useTranslation } from '@front/model'
import { ButtonAdd, NotReady, Page, TButton, TeamInlineSelect } from '@components'
import { PitchScorecardList } from './PitchScorecardList'
import { EmptyPitchScorecard } from './EmptyPitchScorecard'

function PitchScorecardPage(): ReactElement {
  const { t } = useTranslation()
  const { user, isAdmin, isLoading: isLoadingProfile } = useProfile()
  const [team, setTeam] = useState<TeamId | undefined>()
  const managesTeamSet = new Set(user?.teams?.manages)
  const downlineFilter = (t: TeamData): boolean =>
    isAdmin || isNotEmpty(t.parentTeamIds.filter((t) => managesTeamSet.has(t))) || managesTeamSet.has(t.teamId)

  const {
    data: rubrics,
    refetch,
    isFetching,
    isLoading: isLoadingRubrics,
    error,
  } = useRubrics({ archived: false, scopeId: team })

  const isLoading = isLoadingProfile || isLoadingRubrics || !user
  if (isLoading)
    return <NotReady data={rubrics} error={error} id={t('Conversation Scorecards')} isLoading={isLoading} />

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Conversation Scorecards')}>
        <ButtonAdd component={Link} to={`add?scopeId=${team}`} />
        <TButton
          component={Link}
          icon='archive'
          to={team ? `trash?scopeId=${team}` : 'trash'}
          tooltip={t('View Trash')}
          variant='secondary'
        />
      </Page.Heading>
      <Page.FunctionBar>
        <TeamInlineSelect
          allowDeselect
          onChange={setTeam}
          placeholder='Select a team'
          teamFilter={downlineFilter}
          value={team}
        />
      </Page.FunctionBar>
      <Page.Content scrollArea>
        {isNotEmpty(rubrics) ? (
          <PitchScorecardList rubrics={rubrics} scopeId={team ?? user.companyId} />
        ) : (
          <EmptyPitchScorecard scopeId={team} />
        )}
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/settings/scorecard/')({
  component: PitchScorecardPage,
})
